/**
 * Shared leg-status lookup for order-level surfaces.
 *
 * A surface that renders ONE ROW PER ORDER (orders list, subscribed orders,
 * late tables, documentation tab, recurring template instances) needs each
 * order's leg statuses to show the rollup chip (see
 * `src/lib/order-rollup-status.ts`).
 *
 * Surfaces that render ONE ROW PER LEG (schedule board, driver assignments,
 * tractor day view, expanded legs) must NOT use this — each of their rows is
 * already a single leg and shows that leg's own status.
 *
 * Cost: two batched requests for a whole page of orders (legs, then those
 * legs' active trips) — never one per row. `eapi_status` lives on `trips`,
 * not on `legs`.
 */
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import type { OrderLegStatusSummary } from "@/types/tms";
import { chunk } from "@/lib/chunk";

/** Keep request URLs well under the length limit for long id lists. */
const ID_CHUNK_SIZE = 150;

export type LegSummaryMap = Map<string, OrderLegStatusSummary[]>;

export async function fetchLegStatusSummaries(
  orderIds: string[],
  tenantId: string | null | undefined,
): Promise<LegSummaryMap> {
  const byOrder: LegSummaryMap = new Map();
  const ids = [...new Set(orderIds.filter(Boolean))];
  if (ids.length === 0 || !tenantId) return byOrder;

  type LegRow = Pick<Tables<"legs">, "id" | "order_id" | "driver_status" | "leg_sequence" | "trailer_id">;
  const legRows: LegRow[] = [];
  for (const part of chunk(ids, ID_CHUNK_SIZE)) {
    const { data, error } = await supabase
      .from("legs")
      .select("id, order_id, driver_status, leg_sequence, trailer_id")
      .eq("tenant_id", tenantId)
      .in("order_id", part);
    if (error) throw new Error(error.message || "Unable to load order legs");
    legRows.push(...((data ?? []) as LegRow[]));
  }

  const legIds = legRows.map((r) => r.id).filter(Boolean) as string[];
  const eapiStatusByLeg = new Map<string, string | null>();
  for (const part of chunk(legIds, ID_CHUNK_SIZE)) {
    const { data, error } = await supabase
      .from("trips")
      .select("leg_id, eapi_status")
      .eq("tenant_id", tenantId)
      .eq("is_active", true)
      .in("leg_id", part);
    if (error) throw new Error(error.message || "Unable to load active leg trips");
    for (const t of (data ?? []) as Array<{ leg_id: string | null; eapi_status: string | null }>) {
      if (t.leg_id) eapiStatusByLeg.set(t.leg_id, t.eapi_status ?? null);
    }
  }

  // In leg order, so callers can read "leg N" positionally.
  legRows.sort((a, b) => (a.leg_sequence ?? 0) - (b.leg_sequence ?? 0));
  for (const r of legRows) {
    if (!r.order_id) continue;
    const list = byOrder.get(r.order_id) ?? [];
    list.push({
      driver_status: r.driver_status ?? null,
      eapi_status: r.id ? eapiStatusByLeg.get(r.id) ?? null : null,
      leg_sequence: r.leg_sequence ?? null,
      trailer_id: r.trailer_id ?? null,
    });
    byOrder.set(r.order_id, list);
  }
  return byOrder;
}
